/** Colour maths: parsing, perceptual distance, and nearest-bead matching. */

export interface RGB {
  r: number;
  g: number;
  b: number;
}
export interface LAB {
  L: number;
  a: number;
  b: number;
}

export function parseHex(hex: string): RGB {
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (h.length === 8) h = h.slice(0, 6);
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return { r: 0, g: 0, b: 0 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function toHex({ r, g, b }: RGB): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function rgbToLab({ r, g, b }: RGB): LAB {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const R = lin(r);
  const G = lin(g);
  const B = lin(b);
  // sRGB D65 -> XYZ
  let x = (R * 0.4124564 + G * 0.3575761 + B * 0.1804375) / 0.95047;
  let y = R * 0.2126729 + G * 0.7151522 + B * 0.072175;
  let z = (R * 0.0193339 + G * 0.119192 + B * 0.9503041) / 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  x = f(x);
  y = f(y);
  z = f(z);
  return { L: 116 * y - 16, a: 500 * (x - y), b: 200 * (y - z) };
}

/**
 * CIEDE2000 difference. Slower than a plain Lab distance but noticeably better
 * at picking bead colours — it stops dark navies and blacks collapsing together
 * and stops near-neutrals being matched to saturated beads.
 */
export function deltaE2000(l1: LAB, l2: LAB): number {
  const rad = Math.PI / 180;
  const C1 = Math.hypot(l1.a, l1.b);
  const C2 = Math.hypot(l2.a, l2.b);
  const Cbar = (C1 + C2) / 2;
  const G = 0.5 * (1 - Math.sqrt(Cbar ** 7 / (Cbar ** 7 + 25 ** 7)));
  const a1p = (1 + G) * l1.a;
  const a2p = (1 + G) * l2.a;
  const C1p = Math.hypot(a1p, l1.b);
  const C2p = Math.hypot(a2p, l2.b);
  const h = (a: number, b: number) => {
    if (a === 0 && b === 0) return 0;
    const deg = Math.atan2(b, a) / rad;
    return deg >= 0 ? deg : deg + 360;
  };
  const h1p = h(a1p, l1.b);
  const h2p = h(a2p, l2.b);

  const dLp = l2.L - l1.L;
  const dCp = C2p - C1p;
  let dhp = 0;
  if (C1p * C2p !== 0) {
    dhp = h2p - h1p;
    if (dhp > 180) dhp -= 360;
    else if (dhp < -180) dhp += 360;
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp * rad) / 2);

  const Lbar = (l1.L + l2.L) / 2;
  const Cbarp = (C1p + C2p) / 2;
  let hbarp = h1p + h2p;
  if (C1p * C2p !== 0) {
    if (Math.abs(h1p - h2p) > 180) hbarp += h1p + h2p < 360 ? 360 : -360;
    hbarp /= 2;
  } else {
    hbarp = h1p + h2p;
  }

  const T =
    1 -
    0.17 * Math.cos((hbarp - 30) * rad) +
    0.24 * Math.cos(2 * hbarp * rad) +
    0.32 * Math.cos((3 * hbarp + 6) * rad) -
    0.2 * Math.cos((4 * hbarp - 63) * rad);
  const dTheta = 30 * Math.exp(-(((hbarp - 275) / 25) ** 2));
  const Rc = 2 * Math.sqrt(Cbarp ** 7 / (Cbarp ** 7 + 25 ** 7));
  const Sl = 1 + (0.015 * (Lbar - 50) ** 2) / Math.sqrt(20 + (Lbar - 50) ** 2);
  const Sc = 1 + 0.045 * Cbarp;
  const Sh = 1 + 0.015 * Cbarp * T;
  const Rt = -Math.sin(2 * dTheta * rad) * Rc;

  return Math.sqrt(
    (dLp / Sl) ** 2 + (dCp / Sc) ** 2 + (dHp / Sh) ** 2 + Rt * (dCp / Sc) * (dHp / Sh),
  );
}

export function relativeLuminance({ r, g, b }: RGB): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Black or white, whichever stays readable on the given background. */
export function contrastInk(hex: string): string {
  return relativeLuminance(parseHex(hex)) > 0.42 ? '#111111' : '#ffffff';
}

export function mixHex(a: string, b: string, t: number): string {
  const A = parseHex(a);
  const B = parseHex(b);
  return toHex({ r: A.r + (B.r - A.r) * t, g: A.g + (B.g - A.g) * t, b: A.b + (B.b - A.b) * t });
}

/** Fast repeated nearest-colour lookups against a fixed set of bead colours. */
export class ColorMatcher {
  private labs: LAB[];
  private cache = new Map<number, number>();

  constructor(private hexes: string[]) {
    this.labs = hexes.map((h) => rgbToLab(parseHex(h)));
  }

  get size(): number {
    return this.hexes.length;
  }

  /** Index of the closest colour to this rgb, or -1 if the set is empty. */
  nearest(rgb: RGB): number {
    if (this.labs.length === 0) return -1;
    const key = (rgb.r << 16) | (rgb.g << 8) | rgb.b;
    const hit = this.cache.get(key);
    if (hit !== undefined) return hit;
    const lab = rgbToLab(rgb);
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < this.labs.length; i++) {
      const d = deltaE2000(lab, this.labs[i]);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (this.cache.size < 200_000) this.cache.set(key, best);
    return best;
  }

  rgbOf(index: number): RGB {
    return parseHex(this.hexes[index]);
  }
}
